import * as TabsPrimitive from '@radix-ui/react-tabs';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';

import { cn } from '@/shared/lib/utils';

export const Tabs = TabsPrimitive.Root;

/**
 * Tabs alineados al lenguaje visual de Cards (que es el chrome dominante
 * en todas las pages del producto).
 *
 *   TabsList:    rounded-2xl + border-border + bg-card + shadow-sm
 *                (idéntico a `<Card>` — los tabs viven como un chip-bar
 *                dentro del mismo sistema de contenedores que el resto
 *                de las secciones, no como un widget ajeno).
 *   TabsTrigger: rounded-xl (12px = --radius, el mismo de `<Button>`).
 *                Activo: `bg-primary/10 + text-primary + font-semibold`
 *                — patrón soft-primary que ya usan TableActionButton y
 *                FilterBar chips. Hover inactivo: bg-muted + text-foreground.
 *
 * Tipografía: text-sm + font-medium (activo sube a font-semibold) — mismo
 * stack que Button y resto de controles interactivos del DS.
 *
 * Responsive: el caller envuelve `TabsList` en un contenedor con
 * `overflow-x-auto` cuando las labels pueden exceder el ancho disponible
 * (ej. "Permisos individuales" en mobile portrait).
 */
export const TabsList = forwardRef<
  ElementRef<typeof TabsPrimitive.List>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      'inline-flex h-12 items-center justify-center gap-1 rounded-[16px] border border-border bg-card p-1.5 text-muted-foreground shadow-sm',
      className,
    )}
    {...props}
  />
));
TabsList.displayName = 'TabsList';

export const TabsTrigger = forwardRef<
  ElementRef<typeof TabsPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'inline-flex h-9 items-center justify-center gap-2 whitespace-nowrap rounded-[12px] px-4 text-sm font-medium transition-colors',
      'hover:bg-muted hover:text-foreground',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
      'disabled:pointer-events-none disabled:opacity-50',
      'data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:font-semibold data-[state=active]:ring-1 data-[state=active]:ring-inset data-[state=active]:ring-primary/20 data-[state=active]:hover:bg-primary/15',
      '[&_svg]:size-4 [&_svg]:shrink-0',
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = 'TabsTrigger';

export const TabsContent = forwardRef<
  ElementRef<typeof TabsPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn('mt-4 focus-visible:outline-none', className)}
    {...props}
  />
));
TabsContent.displayName = 'TabsContent';
